import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { AppLauncher } from '@capacitor/app-launcher';
import { Capacitor } from '@capacitor/core';
import { useIonRouter } from '@ionic/react';
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { closeStripeBrowser } from '@/core/billing/redirect-to-stripe';
import {
  claimDeepLink,
  markLaunchUrlFetchStarted,
} from '@/core/lib/deep-link-dedupe';
import { classifyNativeLink } from '@/core/lib/native-deep-link';
import {
  navigateShellDeepLink,
  type ShellIonRouter,
} from '@/core/lib/navigate-shell-deep-link';
import { resolveNotificationDeepLink } from '@/core/lib/notification-deep-link';
import { scanExchangeDebug } from '@/core/lib/scan-exchange-debug';
import { useDictionary } from '@/core/providers/dictionary-provider';
import {
  canonicalizeShellPathname,
  shellPaths,
  TABS_PREFIX,
} from '@/core/router/app-paths';
import { isAppSessionActive } from '@/core/router/auth-route-gate';
import { useAuthStore, type AuthStatus } from '@/core/store/auth.store';
import {
  openLinkResolveFromHash,
  openLinkResolveFromProfile,
  openLinkResolveFromProfileHash,
} from '@/core/store/link-resolve.store';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import { delay } from '@/core/utils/timeout.utils';

async function openInDefaultBrowser(url: string): Promise<void> {
  await AppLauncher.openUrl({ url });
}

/**
 * Optional navigation nudge for authenticated users already outside tabs.
 * `LinkResolveDialog` mounts in `DictionaryProviderInner` (auth + shell), so
 * guests stay on the current auth screen — no shell hop required.
 */
function ensureShellForLinkResolve(
  authStatus: AuthStatus,
  currentPathname: string,
  profile?: { workspaceUsername: string; username: string },
): string | null {
  if (currentPathname.startsWith(TABS_PREFIX)) {
    return null;
  }
  if (profile && isAppSessionActive(authStatus)) {
    return shellPaths.leadsPublicProfilePath(
      profile.workspaceUsername,
      profile.username,
    );
  }
  if (isAppSessionActive(authStatus)) {
    return shellPaths.home;
  }
  return null;
}

/**
 * Mount inside {@link DictionaryProvider} so deep-link copy can be localized.
 */
export function DeepLinkListener() {
  useDeepLink();
  return null;
}

/**
 * Hook to handle deep links in Capacitor app.
 */
/**
 * Web / e2e entry for the same product modal Cap opens on scan / Universal Link.
 * `?resolveHash=<code>` opens {@link openLinkResolveFromHash} then strips the param.
 */
const RESOLVE_HASH_QUERY = 'resolveHash';

export function useDeepLink() {
  const dict = useDictionary().deepLink;
  const router = useIonRouter();
  const shellRouter = router as unknown as ShellIonRouter;
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const authStatus = useAuthStore((state) => state.authStatus);
  const listenerRef = useRef<Awaited<
    ReturnType<typeof App.addListener>
  > | null>(null);
  const lastResolvedHashQueryRef = useRef<string | null>(null);

  const getNavigationContext = useCallback(
    () => ({
      currentPathname: canonicalizeShellPathname(location.pathname),
      currentSearch: location.search,
      isNative: isCapacitorNative(),
    }),
    [location.pathname, location.search],
  );

  const processDeepLink = useCallback(
    async (urlString: string) => {
      if (!claimDeepLink(urlString)) {
        scanExchangeDebug('deep-link.deduped', { url: urlString });
        return;
      }

      try {
        const url = new URL(urlString);
        const destination = classifyNativeLink(url);
        scanExchangeDebug('deep-link.received', {
          url: urlString,
          destinationKind: destination.kind,
        });

        if (destination.kind === 'external') {
          await openInDefaultBrowser(destination.url);
          return;
        }

        await delay(500);

        if (destination.kind === 'profile') {
          openLinkResolveFromProfile(
            destination.workspaceUsername,
            destination.username,
          );
          scanExchangeDebug('deep-link.open-link-resolve-profile', {
            workspaceUsername: destination.workspaceUsername,
            username: destination.username,
            ...getNavigationContext(),
          });
          const shim = ensureShellForLinkResolve(
            authStatus,
            getNavigationContext().currentPathname,
            {
              workspaceUsername: destination.workspaceUsername,
              username: destination.username,
            },
          );
          if (shim) {
            await navigateShellDeepLink(
              shellRouter,
              shim,
              getNavigationContext(),
            );
          }
          return;
        }

        if (destination.kind === 'profile-hash') {
          openLinkResolveFromProfileHash(destination.hash, destination.src);
          scanExchangeDebug('deep-link.open-link-resolve-profile-hash', {
            hash: destination.hash,
            src: destination.src ?? null,
            ...getNavigationContext(),
          });
          const shim = ensureShellForLinkResolve(
            authStatus,
            getNavigationContext().currentPathname,
          );
          if (shim) {
            await navigateShellDeepLink(
              shellRouter,
              shim,
              getNavigationContext(),
            );
          }
          return;
        }

        if (destination.kind === 'product') {
          openLinkResolveFromHash(destination.hash, destination.productType);
          scanExchangeDebug('deep-link.open-link-resolve-hash', {
            hash: destination.hash,
            productType: destination.productType ?? null,
            ...getNavigationContext(),
          });
          const shim = ensureShellForLinkResolve(
            authStatus,
            getNavigationContext().currentPathname,
          );
          if (shim) {
            await navigateShellDeepLink(
              shellRouter,
              shim,
              getNavigationContext(),
            );
          }
          return;
        }

        const resolvedPath = resolveNotificationDeepLink(destination.path);
        if (user && resolvedPath.startsWith('/auth')) {
          return;
        }
        // Stripe Checkout/Portal returns via Universal Link while the
        // Capacitor Browser sheet may still be open — dismiss it.
        await closeStripeBrowser();
        if (resolvedPath.startsWith('/tabs')) {
          await navigateShellDeepLink(
            shellRouter,
            resolvedPath,
            getNavigationContext(),
          );
        } else {
          router.push(resolvedPath, 'root', 'replace');
        }
      } catch (error) {
        scanExchangeDebug('deep-link.failed', {
          url: urlString,
          error:
            error instanceof Error
              ? { name: error.name, message: error.message }
              : String(error),
        });
        toast.error(dict.failed);
      }
    },
    [dict, router, shellRouter, user, authStatus, getNavigationContext],
  );

  const processDeepLinkRef = useRef(processDeepLink);
  useEffect(() => {
    processDeepLinkRef.current = processDeepLink;
  });

  // Browser / Playwright: simulate scan deeplink without Capacitor App URLs.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const hash = params.get(RESOLVE_HASH_QUERY)?.trim() ?? '';
    if (!hash) {
      lastResolvedHashQueryRef.current = null;
      return;
    }
    const dedupeKey = `${location.pathname}?${hash}`;
    if (lastResolvedHashQueryRef.current === dedupeKey) {
      return;
    }
    lastResolvedHashQueryRef.current = dedupeKey;
    openLinkResolveFromHash(hash);
    params.delete(RESOLVE_HASH_QUERY);
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : '',
        hash: location.hash,
      },
      { replace: true },
    );
  }, [location.pathname, location.search, location.hash, navigate]);

  useEffect(() => {
    const isCapacitor = Capacitor.isNativePlatform();
    let cancelled = false;

    if (!isCapacitor) {
      return;
    }

    const handleDeepLink = (event: URLOpenListenerEvent) => {
      void processDeepLinkRef.current(event.url);
    };

    const setupDeepLinks = async () => {
      try {
        const listener = await App.addListener('appUrlOpen', handleDeepLink);
        if (cancelled) {
          await listener.remove();
          return;
        }
        listenerRef.current = listener;
      } catch {
        // Silently handle errors
      }

      if (!markLaunchUrlFetchStarted()) {
        return;
      }

      await delay(500);

      if (cancelled) {
        return;
      }

      try {
        const result = await App.getLaunchUrl();
        if (result?.url) {
          await processDeepLinkRef.current(result.url);
        }
      } catch {
        // No launch URL
      }
    };

    void setupDeepLinks();

    return () => {
      cancelled = true;
      if (listenerRef.current) {
        listenerRef.current.remove().catch(() => {
          // Ignore cleanup errors
        });
        listenerRef.current = null;
      }
    };
  }, []);
}
